import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router";
import {
    Box,
    Container,
    Typography,
    Button,
    Divider,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Switch,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbarContainer, GridActionsCellItem, type GridRowParams } from "@mui/x-data-grid";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { Loading } from "src/components/utils/Loading";
import { useSnackbar } from "src/utils/useSnackbar";
import { useGetCustomThemes, type CustomThemeListItem } from "src/api/client-api/customTheme/useGetCustomThemes";
import { useDeleteCustomTheme } from "src/api/client-api/customTheme/useDeleteCustomTheme";
import { useActivateCustomTheme } from "src/api/client-api/customTheme/useActivateCustomTheme";
import { useDeactivateCustomTheme } from "src/api/client-api/customTheme/useDeactivateCustomTheme";
import { useTranslation } from "react-i18next";
import { CHANGE, CREATE, CUSTOM_THEME, DELETE, MODIFY, useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";

export const CustomThemesList = () => {
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const canCreateCustomTheme = canUser(CREATE, CUSTOM_THEME);
    const canModifyCustomTheme = canUser(MODIFY, CUSTOM_THEME);
    const canDeleteCustomTheme = canUser(DELETE, CUSTOM_THEME);
    const canChangeCustomTheme = canUser(CHANGE, CUSTOM_THEME);
    const [paginationModel, setPaginationModel] = useState<{ page: number; pageSize: number }>({
        page: 0,
        pageSize: 10,
    });

    const { customThemes, totalItems, isLoading } = useGetCustomThemes({
        itemsPerPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
    });

    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const deleteTheme = useDeleteCustomTheme();
    const activateTheme = useActivateCustomTheme();
    const deactivateTheme = useDeactivateCustomTheme();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);

    const handlePaginationModelChange = (paginationModel: { page: number; pageSize: number }) => {
        setPaginationModel(paginationModel);
    };

    const handleDeleteClick = (id: number | string) => () => {
        if (typeof id === "string") return;
        setIdToDelete(id);
        setDisplayDialog(true);
    };

    const handleStatusChange = (id: number | string, currentStatus: boolean) => () => {
        if (typeof id === "string") return;

        if (currentStatus) {
            // Désactiver le thème
            deactivateTheme.mutate(id, {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        type: "success",
                        message: t("settings.customThemes.deactivateDialog.success", "Thème désactivé avec succès"),
                    });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.customThemes.deactivateDialog.error", "Erreur lors de la désactivation"),
                    });
                },
            });
        } else {
            // Activer le thème
            activateTheme.mutate(id, {
                onSuccess: () => {
                    snackbar.setSnackbarProps({
                        type: "success",
                        message: t("settings.customThemes.activateDialog.success", "Thème activé avec succès"),
                    });
                },
                onError: () => {
                    snackbar.setSnackbarProps({
                        type: "error",
                        message: t("settings.customThemes.activateDialog.error", "Erreur lors de l'activation"),
                    });
                },
            });
        }
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Nom",
            flex: 2,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, whiteSpace: "break-spaces" }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "description",
            headerName: "Description",
            flex: 3,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography variant="body2" sx={{ whiteSpace: "break-spaces" }}>
                        {params.value || "Sans description"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "baseTheme",
            headerName: "Thème de base",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Chip label={params.value} size="small" variant="outlined" />
                </Box>
            ),
        },
        {
            field: "isActive",
            headerName: "Statut",
            flex: 1,
            renderCell: (params) => {
                const isActive = Boolean(params.value);
                const { id } = params.row;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color={isActive ? "success.main" : "text.secondary"}>
                                {isActive ?
                                    t("settings.customThemes.active", "Actif")
                                :   t("settings.customThemes.inactive", "Inactif")}
                            </Typography>
                            {canChangeCustomTheme && (
                                <Switch
                                    checked={isActive}
                                    onChange={handleStatusChange(id, isActive)}
                                    size="small"
                                    disabled={activateTheme.isLoading || deactivateTheme.isLoading}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: (theme) => theme.palette.primary[600],
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: (theme) => theme.palette.primary[600],
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                );
            },
        },
        ...(canModifyCustomTheme || canDeleteCustomTheme ?
            [
                {
                    field: "actions",
                    type: "actions" as const,
                    headerName: "Actions",
                    width: 120,
                    cellClassName: "actions",
                    getActions: ({ id }: GridRowParams) => [
                        ...(canModifyCustomTheme ?
                            [
                                <GridActionsCellItem
                                    icon={
                                        <IconButton
                                            component={RouterLink}
                                            to={URLHandler.settings.customTheme.edit(Number(id))}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    }
                                    label="Edit"
                                    className="textPrimary"
                                    color="inherit"
                                    key={`${id}-custom-theme-edit-action`}
                                />,
                            ]
                        :   []),
                        ...(canDeleteCustomTheme ?
                            [
                                <GridActionsCellItem
                                    icon={<DeleteIcon />}
                                    label="Delete"
                                    onClick={handleDeleteClick(id.valueOf())}
                                    color="inherit"
                                    key={`${id}-custom-theme-delete-action`}
                                />,
                            ]
                        :   []),
                    ],
                },
            ]
        :   []),
    ];

    const EditToolbar = () => {
        const handleClick = () => {
            navigate(URLHandler.settings.customTheme.create());
        };

        return (
            <GridToolbarContainer>
                <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                    {t("settings.customThemes.create", "Créer un nouveau thème")}
                </Button>
            </GridToolbarContainer>
        );
    };

    if (customThemes === undefined) return <Loading />;

    const rows = customThemes.map((theme: CustomThemeListItem) => {
        return {
            id: theme.id,
            name: theme.name,
            description: theme.description,
            baseTheme: theme.baseTheme,
            isActive: theme.isActive ?? false, // Handle undefined
        };
    });

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography component="h1" variant="h2">
                        {t("settings.customThemes", "Thèmes Personnalisés")}
                    </Typography>
                </Stack>
            </Stack>
            <Box sx={{ py: 2 }}></Box>
            <Divider />
            <Box sx={{ py: 2 }}></Box>

            <Dialog maxWidth="xs" open={displayDialog}>
                <DialogTitle>{t("settings.customThemes.deleteDialog.title", "Êtes-vous sûr ?")}</DialogTitle>
                <DialogContent dividers>
                    {t(
                        "settings.customThemes.deleteDialog.content",
                        "Cliquez sur 'oui' pour supprimer définitivement ce thème personnalisé.",
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisplayDialog(false)}>
                        {t("settings.customThemes.deleteDialog.no", "Non")}
                    </Button>
                    <Button
                        onClick={() => {
                            if (idToDelete) {
                                deleteTheme.mutate(idToDelete, {
                                    onSuccess: () => {
                                        snackbar.setSnackbarProps({
                                            type: "success",
                                            message: t(
                                                "settings.customThemes.deleteDialog.success",
                                                "Thème supprimé avec succès",
                                            ),
                                        });
                                    },
                                    onError: () => {
                                        snackbar.setSnackbarProps({
                                            type: "error",
                                            message: t(
                                                "settings.customThemes.deleteDialog.error",
                                                "Erreur lors de la suppression",
                                            ),
                                        });
                                    },
                                });
                            }
                            setDisplayDialog(false);
                        }}
                    >
                        {t("settings.customThemes.deleteDialog.yes", "Oui")}
                    </Button>
                </DialogActions>
            </Dialog>

            <DataGrid
                rows={rows}
                columns={columns}
                loading={isLoading}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 20]}
                rowCount={totalItems || 0}
                paginationMode="server"
                slots={canCreateCustomTheme ? { toolbar: EditToolbar } : undefined}
                disableRowSelectionOnClick
                sx={{
                    border: 0,
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #f0f0f0",
                        display: "flex",
                        alignItems: "center",
                    },
                }}
            />
        </Container>
    );
};
